import React, { useState } from 'react';

const LoginScreen = ({ onClose, onLogin, initialUserType = 'rider', initialMode = 'login', resetToken = null }) => {
  const [authMode, setAuthMode] = useState(resetToken ? 'reset' : initialMode); // 'login', 'signup', 'forgot', 'reset'
  const [userType, setUserType] = useState(initialUserType);
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', confirmPassword: '', phone: ''
  });
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [resetForm, setResetForm] = useState({ password: '', confirmPassword: '' });
  const [resetSuccess, setResetSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  const isSignUp = authMode === 'signup';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isSignUp) {
        if (userType !== 'rider') {
          setError('Only customer accounts can be created here. Contact admin for driver access.');
          setLoading(false);
          return;
        }

        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        if (formData.password.length < 8) {
          setError('Password must be at least 8 characters');
          setLoading(false);
          return;
        }
        if (!formData.name || !formData.phone) {
          setError('Please fill in all required fields');
          setLoading(false);
          return;
        }
        
        const response = await fetch(`${BACKEND_URL}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...formData,
            userType: 'rider'
          })
        });

        if (response.ok) {
          const data = await response.json();
          localStorage.setItem('authToken', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
          window.dispatchEvent(new CustomEvent('userLoggedIn', { 
            detail: { user: data.user } 
          }));
          onLogin(data.user);
        } else {
          const error = await response.json();
          setError(error.detail || 'Registration failed');
        }
      } else {
        if (!formData.email || !formData.password) {
          setError('Please enter both email and password');
          setLoading(false);
          return;
        }

        const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            expectedUserType: userType
          })
        });

        if (response.ok) {
          const data = await response.json();
          
          if (data.user.userType !== userType) {
            setError(`This account is not authorized for ${userType} access`);
            setLoading(false);
            return;
          }

          localStorage.setItem('authToken', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
          window.dispatchEvent(new CustomEvent('userLoggedIn', { 
            detail: { user: data.user } 
          }));
          onLogin(data.user);
        } else {
          const error = await response.json();
          setError(error.detail || 'Login failed. Please check your credentials.');
        }
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });
      if (response.ok) {
        setForgotSuccess(true);
      } else {
        setError('Failed to process request. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (resetForm.password !== resetForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (resetForm.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, new_password: resetForm.password })
      });
      if (response.ok) {
        setResetSuccess(true);
        // Clear token from URL
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to reset password. The link may have expired.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getUserTypeTitle = () => {
    switch (userType) {
      case 'rider': return 'Customer';
      case 'driver': return 'Driver';
      case 'admin': return 'Administrator';
      default: return 'User';
    }
  };

  // Eye Icon SVG Component
  const EyeIcon = ({ show }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {show ? (
        <>
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
          <circle cx="12" cy="12" r="3"/>
        </>
      ) : (
        <>
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
          <line x1="1" y1="1" x2="23" y2="23"/>
        </>
      )}
    </svg>
  );

  return (
    <div className="auth-fullpage">
      {/* Left Branding Section */}
      <div className="auth-branding-section">
        <div className="auth-branding-content">
          <img 
            src="https://customer-assets.emergentagent.com/job_pwa-vercel-prod/artifacts/z056vcil_Logo.png.png" 
            alt="CompanionCab" 
            className="auth-brand-logo"
          />
          <h1>CompanionCab</h1>
          <p>Caring transportation for those who matter most</p>
          
          <div className="auth-brand-features">
            <div className="auth-brand-feature">
              <span>👴</span>
              <span>Elderly Care</span>
            </div>
            <div className="auth-brand-feature">
              <span>🏥</span>
              <span>Medical Transport</span>
            </div>
            <div className="auth-brand-feature">
              <span>🛒</span>
              <span>Shopping Assistance</span>
            </div>
            <div className="auth-brand-feature">
              <span>💖</span>
              <span>Compassionate Service</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Form Section */}
      <div className="auth-form-section">
        <button className="auth-back-btn" onClick={onClose}>
          ← Back to Home
        </button>

        <div className="auth-form-wrapper">
          <div className="auth-form-header">
            <h2>
              {authMode === 'forgot' ? 'Reset Password' :
               authMode === 'reset' ? 'Set New Password' :
               (isSignUp ? `Create ${getUserTypeTitle()} Account` : `${getUserTypeTitle()} Sign In`)}
            </h2>
            <p>
              {authMode === 'forgot' ? "Enter your email and we'll send you a reset link" :
               authMode === 'reset' ? 'Choose a new password for your account' :
               (isSignUp ? 'Join CompanionCab today' : 'Welcome back! Sign in to continue')}
            </p>
          </div>

          {/* FORGOT PASSWORD FORM */}
          {authMode === 'forgot' && (
            <div className="auth-form">
              {error && <div className="auth-error-msg">{error}</div>}
              {forgotSuccess ? (
                <div className="auth-success-msg">
                  <p>If your email is registered, you will receive reset instructions shortly. Check your inbox.</p>
                  <button type="button" className="auth-submit" onClick={() => { setAuthMode('login'); setForgotSuccess(false); }}>
                    Back to Sign In
                  </button>
                </div>
              ) : (
                <>
                  <div className="auth-field">
                    <label htmlFor="forgot-email">Email Address</label>
                    <input
                      id="forgot-email"
                      name="forgot-email"
                      type="email"
                      autoComplete="email"
                      placeholder="Enter your registered email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                    />
                  </div>
                  <button type="button" className="auth-submit" disabled={loading || !forgotEmail} onClick={handleForgotPassword}>
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                  <div className="auth-toggle-mode">
                    <button type="button" onClick={() => { setAuthMode('login'); setError(''); }}>
                      Back to Sign In
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* RESET PASSWORD FORM */}
          {authMode === 'reset' && (
            <div className="auth-form">
              {error && <div className="auth-error-msg">{error}</div>}
              {resetSuccess ? (
                <div className="auth-success-msg">
                  <p>Your password has been reset successfully!</p>
                  <button type="button" className="auth-submit" onClick={() => { setAuthMode('login'); setResetSuccess(false); }}>
                    Sign In Now
                  </button>
                </div>
              ) : (
                <form onSubmit={handleResetPassword} id="reset-password-form">
                  <div className="auth-field">
                    <label htmlFor="reset-new-password">New Password</label>
                    <div className="auth-password-input">
                      <input
                        id="reset-new-password"
                        name="new_password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        placeholder="Enter new password (min 8 characters)"
                        value={resetForm.password}
                        onChange={(e) => setResetForm({ ...resetForm, password: e.target.value })}
                        required
                      />
                      <button type="button" className="auth-eye-toggle" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                        <EyeIcon show={showPassword} />
                      </button>
                    </div>
                  </div>
                  <div className="auth-field">
                    <label htmlFor="reset-confirm-password">Confirm New Password</label>
                    <div className="auth-password-input">
                      <input
                        id="reset-confirm-password"
                        name="confirm_password"
                        type={showConfirmPassword ? "text" : "password"}
                        autoComplete="new-password"
                        placeholder="Confirm new password"
                        value={resetForm.confirmPassword}
                        onChange={(e) => setResetForm({ ...resetForm, confirmPassword: e.target.value })}
                        required
                      />
                      <button type="button" className="auth-eye-toggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)} tabIndex={-1}>
                        <EyeIcon show={showConfirmPassword} />
                      </button>
                    </div>
                  </div>
                  <button type="submit" className="auth-submit" disabled={loading}>
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </button>
                </form>
              )}
            </div>
          )}

          {/* LOGIN / SIGNUP FORM */}
          {(authMode === 'login' || authMode === 'signup') && (
            <div className="auth-type-tabs">
              <button
                type="button"
                className={`auth-type-tab ${userType === 'rider' ? 'active' : ''}`}
                onClick={() => setUserType('rider')}
              >
                <span>👤</span> Customer
              </button>
              <button
                type="button"
                className={`auth-type-tab ${userType === 'driver' ? 'active' : ''}`}
                onClick={() => setUserType('driver')}
              >
                <span>🚗</span> Driver
              </button>
              <button
                type="button"
                className={`auth-type-tab ${userType === 'admin' ? 'active' : ''}`}
                onClick={() => setUserType('admin')}
              >
                <span>🛠️</span> Admin
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className={`auth-form ${!(authMode === 'login' || authMode === 'signup') ? 'auth-form-hidden' : ''}`}>
            {(authMode === 'login' || authMode === 'signup') && <>
            {error && <div className="auth-error-msg">{error}</div>}

            {/* Name Field (Sign Up Only) */}
            {isSignUp && userType === 'rider' && (
              <div className="auth-field">
                <label htmlFor="login-name">Full Name</label>
                <input
                  id="login-name"
                  type="text"
                  name="name"
                  autoComplete="name"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
            )}

            {/* Email Field */}
            <div className="auth-field">
              <label htmlFor="login-email">Email Address</label>
              <input
                id="login-email"
                type="email"
                name="email"
                autoComplete="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            {/* Password Field */}
            <div className="auth-field">
              <label htmlFor="login-password">Password</label>
              <div className="auth-password-input">
                <input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  autoComplete={isSignUp ? "new-password" : "current-password"}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <button 
                  type="button" 
                  className="auth-eye-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  <EyeIcon show={showPassword} />
                </button>
              </div>
              {isSignUp && <small className="auth-hint">Must be at least 8 characters</small>}
              {!isSignUp && (
                <div className="auth-forgot-link">
                  <button type="button" onClick={() => { setAuthMode('forgot'); setError(''); }}>
                    Forgot Password?
                  </button>
                </div>
              )}
            </div>

            {/* Confirm Password (Sign Up Only) */}
            {isSignUp && userType === 'rider' && (
              <div className="auth-field">
                <label htmlFor="login-confirm-password">Confirm Password</label>
                <div className="auth-password-input">
                  <input
                    id="login-confirm-password"
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    autoComplete="new-password"
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                  />
                  <button 
                    type="button" 
                    className="auth-eye-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex={-1}
                  >
                    <EyeIcon show={showConfirmPassword} />
                  </button>
                </div>
              </div>
            )}

            {/* Phone Field (Sign Up Only) */}
            {isSignUp && userType === 'rider' && (
              <div className="auth-field">
                <label htmlFor="login-phone">Phone Number</label>
                <input
                  id="login-phone"
                  type="tel"
                  name="phone"
                  autoComplete="tel"
                  placeholder="Enter your phone number"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>
            )}

            {/* Submit Button */}
            <button 
              type="submit" 
              className="auth-submit"
              disabled={loading || (isSignUp && userType !== 'rider')}
              data-testid="auth-submit-btn"
            >
              {loading ? 'Please wait...' : (isSignUp ? 'Create Account' : 'Sign In')}
            </button>

            {/* Toggle Sign Up/Sign In */}
            {userType === 'rider' && (
              <div className="auth-toggle-mode">
                <span>{isSignUp ? 'Already have an account?' : "Don't have an account?"}</span>
                <button
                  type="button"
                  onClick={() => { setAuthMode(isSignUp ? 'login' : 'signup'); setError(''); }}
                  disabled={loading}
                >
                  {isSignUp ? 'Sign In' : 'Create Account'}
                </button>
              </div>
            )}

            {userType !== 'rider' && !isSignUp && (
              <div className="auth-staff-info">
                <p>Enter your credentials above to access your staff account.</p>
              </div>
            )}
            </>}
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
